// One row in RulesPanel: drag handle · name/summary · badges · last-run line
// · enabled Switch · overflow Menu (Edit / Run history / Run now / Delete).

import { needsPackage, ruleSummary } from '@tinycld/core/components/rules/rule-summary'
import { SortableDragHandle } from '@tinycld/core/components/SortableList'
import type { CatalogResponse } from '@tinycld/core/lib/automation/api'
import { useRuleMutations } from '@tinycld/core/lib/automation/use-rule-mutations'
import { formatTimeAgo } from '@tinycld/core/lib/format-utils'
import { useRulesUiStore } from '@tinycld/core/lib/stores/rules-ui-store'
import { useThemeColor } from '@tinycld/core/lib/use-app-theme'
import type { Rules } from '@tinycld/core/types/pbSchema'
import { ConfirmDialog } from '@tinycld/core/ui/ConfirmDialog'
import { Menu } from '@tinycld/core/ui/menu'
import { Switch } from '@tinycld/core/ui/switch'
import { Clock, History, MoreVertical, Pencil, Play, Trash2 } from 'lucide-react-native'
import { useEffect, useRef, useState } from 'react'
import { Pressable, Text, View } from 'react-native'

export interface RuleRowProps {
    rule: Rules
    catalog: CatalogResponse
    canEdit: boolean
    /** Newest run for this rule, if any — resolved once at the panel level. */
    lastRun?: { fired_at: string; matched: boolean }
    /** Show an "Organization" pill — set by the panel only in mixed contexts. */
    showScopeBadge?: boolean
}

// synthetic-trigger rules (manual/schedule) are the only ones "Run now" makes
// sense for — everything else fires off a real collection event the engine
// can't synthesize on demand.
function isSyntheticTrigger(rule: Rules, catalog: CatalogResponse): boolean {
    return Boolean(catalog.triggers.find(t => t.ref === rule.trigger)?.synthetic)
}

export function RuleRow({ rule, catalog, canEdit, lastRun, showScopeBadge }: RuleRowProps) {
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
    const { setEnabled, remove } = useRuleMutations()
    const openEdit = useRulesUiStore(s => s.openEdit)
    const openHistory = useRulesUiStore(s => s.openHistory)

    const missingPkg = needsPackage(rule, catalog)
    const summary = ruleSummary(rule, catalog)

    return (
        <View
            className="flex-row items-center gap-3 px-3 py-3 border-b border-border"
            style={{ opacity: missingPkg ? 0.6 : 1 }}
        >
            <SortableDragHandle disabled={!canEdit} />

            <RuleRowMain
                rule={rule}
                summary={summary}
                missingPkg={missingPkg}
                showScopeBadge={Boolean(showScopeBadge)}
                lastRun={lastRun}
            />

            <Switch
                value={rule.enabled}
                onValueChange={enabled => setEnabled.mutate({ id: rule.id, enabled })}
                disabled={!canEdit}
                accessibilityLabel={`${rule.enabled ? 'Disable' : 'Enable'} ${rule.name}`}
            />

            <RuleRowMenu
                rule={rule}
                canEdit={canEdit}
                canRunNow={isSyntheticTrigger(rule, catalog)}
                onEdit={() => openEdit(rule.id)}
                onOpenHistory={() => openHistory(rule.id)}
                onRequestDelete={() => setIsConfirmingDelete(true)}
            />

            <ConfirmDialog
                isOpen={isConfirmingDelete}
                onClose={() => setIsConfirmingDelete(false)}
                onConfirm={() => {
                    remove.mutate(rule.id)
                    setIsConfirmingDelete(false)
                }}
                title={`Delete "${rule.name}"?`}
                message="This cannot be undone."
                confirmLabel="Delete"
                isDestructive
                isSubmitting={remove.isPending}
            />
        </View>
    )
}

function RuleRowMain({
    rule,
    summary,
    missingPkg,
    showScopeBadge,
    lastRun,
}: {
    rule: Rules
    summary: string
    missingPkg: string | null
    showScopeBadge: boolean
    lastRun?: { fired_at: string; matched: boolean }
}) {
    return (
        <View className="flex-1 gap-0.5 min-w-0">
            <View className="flex-row items-center gap-2">
                <Text className="text-sm font-semibold text-foreground" numberOfLines={1}>
                    {rule.name}
                </Text>
                <ScopeBadge isVisible={showScopeBadge} />
                <NeedsPackageBadge pkg={missingPkg} />
            </View>
            <Text className="text-muted text-sm" numberOfLines={1}>
                {summary}
            </Text>
            <LastRunLine lastRun={lastRun} />
        </View>
    )
}

function ScopeBadge({ isVisible }: { isVisible: boolean }) {
    if (!isVisible) return null
    return (
        <View className="px-1.5 py-0.5 rounded-full bg-info">
            <Text className="text-[10px] font-semibold text-info-foreground">Organization</Text>
        </View>
    )
}

function NeedsPackageBadge({ pkg }: { pkg: string | null }) {
    if (!pkg) return null
    return (
        <View className="px-1.5 py-0.5 rounded-full bg-warning-soft">
            <Text className="text-[10px] font-semibold text-warning-soft-foreground">
                needs {pkg}
            </Text>
        </View>
    )
}

function LastRunLine({ lastRun }: { lastRun?: { fired_at: string; matched: boolean } }) {
    const mutedColor = useThemeColor('muted-foreground')
    if (!lastRun) return null
    return (
        <View className="flex-row items-center gap-1">
            <Clock size={11} color={mutedColor} />
            <Text className="text-[11px] text-muted-foreground">
                {lastRun.matched ? 'Ran' : "Didn't match"} {formatTimeAgo(lastRun.fired_at)}
            </Text>
        </View>
    )
}

function RuleRowMenu({
    rule,
    canEdit,
    canRunNow,
    onEdit,
    onOpenHistory,
    onRequestDelete,
}: {
    rule: Rules
    canEdit: boolean
    canRunNow: boolean
    onEdit: () => void
    onOpenHistory: () => void
    onRequestDelete: () => void
}) {
    const { runNow } = useRuleMutations()
    const mutedColor = useThemeColor('muted-foreground')
    const [isQueued, setIsQueued] = useState(false)
    const queuedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // A pending timeout must not fire setState after this row unmounts (menu
    // closed, rule deleted, list re-filtered) — clear it on unmount, and also
    // clear any prior pending timeout before scheduling a new one so rapid
    // repeat "Run now" clicks can't leave stale timers behind.
    useEffect(() => {
        return () => {
            if (queuedTimeoutRef.current) clearTimeout(queuedTimeoutRef.current)
        }
    }, [])

    // notify.emit requires a registered event name (no "rule run queued" event
    // exists in the typed registry), so feedback is transient local UI state
    // rather than a toast — the menu item label swaps to "Queued ✓" for 2s.
    // Only flips on success: on failure the default onError toast already
    // reports it, and a "Queued ✓" label the run never actually queued would
    // be misleading.
    const handleRunNow = () => {
        runNow.mutate(rule.id, {
            onSuccess: () => {
                if (queuedTimeoutRef.current) clearTimeout(queuedTimeoutRef.current)
                setIsQueued(true)
                queuedTimeoutRef.current = setTimeout(() => setIsQueued(false), 2000)
            },
        })
    }

    return (
        <Menu
            trigger={
                <Pressable className="p-1.5 rounded-full" accessibilityLabel="More actions">
                    <MoreVertical size={16} color={mutedColor} />
                </Pressable>
            }
            title={rule.name}
        >
            <Menu.Item label="Edit" icon={Pencil} onSelect={onEdit} isDisabled={!canEdit} />
            <Menu.Item label="Run history" icon={History} onSelect={onOpenHistory} />
            <RunNowMenuItem
                isVisible={canRunNow && canEdit}
                isQueued={isQueued}
                onPress={handleRunNow}
            />
            <Menu.Separator />
            <Menu.Item
                label="Delete"
                icon={Trash2}
                onSelect={onRequestDelete}
                isDisabled={!canEdit}
                isDestructive
            />
        </Menu>
    )
}

function RunNowMenuItem({
    isVisible,
    isQueued,
    onPress,
}: {
    isVisible: boolean
    isQueued: boolean
    onPress: () => void
}) {
    if (!isVisible) return null
    return <Menu.Item label={isQueued ? 'Queued ✓' : 'Run now'} icon={Play} onSelect={onPress} />
}
